import { useEffect, useMemo, useState, type FormEvent } from 'react';
import BrandLogo from '../components/BrandLogo';
import {
  api,
  getHouseCode,
  queuePrivateHouse,
  setHouseCode,
  type User,
} from '../lib/api';
import { CHARACTER_IDS, CHARACTER_META, type CharacterId } from '../theater/catalog';
import './LoginPage.css';

const THEATER_HOME = 'https://zemew.com/theatre/';
const DEFAULT_SSO = '/sso/theater/?return=' + encodeURIComponent(THEATER_HOME);

function ticketFromLocation(): string {
  return new URLSearchParams(window.location.search).get('ticket') ?? '';
}

function hostFromLocation(): boolean {
  return new URLSearchParams(window.location.search).get('host') === '1';
}

function normalizeJoinCode(raw: string): string | null {
  const code = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return /^[A-Z0-9]{4}$/.test(code) ? code : null;
}

function persistJoinCode(raw: string): void {
  const trimmed = raw.trim();
  if (!trimmed) {
    setHouseCode(null);
    return;
  }
  const code = normalizeJoinCode(trimmed);
  if (code) setHouseCode(code);
}

function withRoomReturn(ssoUrl: string, code: string): string {
  const clean = normalizeJoinCode(code);
  const returnTo = clean ? `${THEATER_HOME}?room=${clean}` : THEATER_HOME;
  try {
    const url = new URL(ssoUrl, window.location.origin);
    url.searchParams.set('return', returnTo);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return ssoUrl;
  }
}

export default function LoginPage({
  onLogin,
  onWatchInstead,
}: {
  onLogin: (user: User) => void;
  onWatchInstead?: () => void;
}) {
  const [mode, setMode] = useState<'join' | 'host'>(hostFromLocation() ? 'host' : 'join');
  const initialTicket = useMemo(ticketFromLocation, []);
  const [ticket, setTicket] = useState(initialTicket);
  const [memberName, setMemberName] = useState<string | null>(null);
  const [ssoUrl, setSsoUrl] = useState(DEFAULT_SSO);
  const [ssoReady, setSsoReady] = useState(true);
  const [checkingZemew, setCheckingZemew] = useState(!initialTicket && !hostFromLocation());
  const [zemewLoggedIn, setZemewLoggedIn] = useState(false);
  const [ssoFailed, setSsoFailed] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [characterId, setCharacterId] = useState<CharacterId>('normal');
  const [partyCode, setPartyCode] = useState(() => getHouseCode() ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ssoHref = withRoomReturn(ssoUrl, partyCode);
  const publicSsoHref = withRoomReturn(ssoUrl, '');

  useEffect(() => {
    if (ticket || hostFromLocation() || ssoFailed) {
      setCheckingZemew(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const cfg = await api.authConfig();
        if (cancelled) return;
        setSsoUrl(cfg.zemewSsoUrl);
        setSsoReady(cfg.ssoReady);
      } catch {
        // Keep the same-origin SSO URL.
      }

      try {
        const res = await fetch('/sso/theater/status.php', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const data = (await res.json()) as { loggedIn?: boolean };
        if (cancelled) return;
        setZemewLoggedIn(Boolean(data.loggedIn));
      } catch {
        // Fall through to the manual sign-in card.
      }

      if (!cancelled) setCheckingZemew(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [ticket, ssoFailed]);

  useEffect(() => {
    if (!ticket) return;
    setBusy(true);
    void api
      .previewZemew(ticket)
      .then((res) => {
        setMemberName(res.username);
        setZemewLoggedIn(true);
        setError(null);
        const url = new URL(window.location.href);
        url.searchParams.delete('ticket');
        window.history.replaceState({}, '', url.pathname + url.search);
      })
      .catch((err) => {
        setTicket('');
        setMemberName(null);
        setSsoFailed(true);
        setError((err as Error).message || 'Zemew sign-in expired. Sign in again.');
      })
      .finally(() => setBusy(false));
  }, [ticket]);

  function goToSso(code = partyCode) {
    persistJoinCode(code);
    window.location.assign(withRoomReturn(ssoUrl, code));
  }

  function startPrivateHouse() {
    persistJoinCode('');
    queuePrivateHouse();
    if (ticket) return;
    window.location.assign(publicSsoHref);
  }

  function joinPrivateHouse() {
    const code = normalizeJoinCode(partyCode);
    if (!code) {
      setError('Enter a 4-character join code.');
      return;
    }
    persistJoinCode(code);
    if (ticket) return;
    if (zemewLoggedIn) {
      goToSso(code);
      return;
    }
    onWatchInstead?.();
  }

  async function handleJoin(e: FormEvent) {
    e.preventDefault();
    if (!ticket) return;
    persistJoinCode(partyCode);
    setBusy(true);
    setError(null);
    try {
      const res = await api.joinZemew(ticket, characterId);
      onLogin(res.user);
    } catch (err) {
      setError((err as Error).message || 'Could not enter the theater.');
    } finally {
      setBusy(false);
    }
  }

  async function handleHostLogin(e: FormEvent) {
    e.preventDefault();
    persistJoinCode(partyCode);
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(username, password);
      onLogin(res.user);
    } catch (err) {
      setError((err as Error).message || 'Invalid username or password.');
    } finally {
      setBusy(false);
    }
  }

  function openHostLogin() {
    persistJoinCode(partyCode);
    setMode('host');
    setCheckingZemew(false);
    setError(null);
    setPassword('');
    const url = new URL(window.location.href);
    url.searchParams.delete('ticket');
    url.searchParams.set('host', '1');
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  const joinRow = (
    <div className="login-join">
      <span className="login-field-label">Join a private house</span>
      <div className="login-join-row">
        <input
          className="login-code-input"
          value={partyCode}
          maxLength={4}
          spellCheck={false}
          autoCapitalize="characters"
          autoComplete="off"
          placeholder="ABCD"
          aria-label="Private house join code"
          onChange={(e) =>
            setPartyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))
          }
        />
        <button
          className="btn"
          type="button"
          disabled={busy || partyCode.length !== 4}
          onClick={() => joinPrivateHouse()}
        >
          Join
        </button>
      </div>
    </div>
  );

  return (
    <div className="login-page">
      <div className="login-veil" />
      <div className={`login-card panel fade-up${memberName ? ' login-card-wide' : ''}`}>
        <a className="login-back" href="https://zemew.com/">
          Back to Zemew
        </a>
        <h1 className="brand">
          <BrandLogo homeLink={false} />
        </h1>
        <p className="login-kicker">Watch parties</p>

        {mode === 'join' ? (
          <>
            {memberName ? (
              <>
                <p className="muted login-sub">
                  Joining as <strong>{memberName}</strong>. Pick a character, then enter the theater.
                </p>
                <form onSubmit={handleJoin} className="login-form">
                  <div>
                    <span className="login-field-label">Character</span>
                    <div className="character-grid login-characters">
                      {CHARACTER_IDS.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={`character-pick${characterId === id ? ' is-active' : ''}`}
                          onClick={() => setCharacterId(id)}
                        >
                          <span className="character-emoji">{CHARACTER_META[id].emoji}</span>
                          <span>{CHARACTER_META[id].name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {error && <div className="error-banner">{error}</div>}
                  <button className="btn btn-primary login-enter-btn" disabled={busy} type="submit">
                    {busy ? 'Entering…' : 'Enter the theater'}
                  </button>
                  <button
                    className="btn btn-gold login-enter-btn"
                    type="submit"
                    disabled={busy}
                    onClick={() => {
                      queuePrivateHouse();
                      persistJoinCode('');
                    }}
                  >
                    Create private theatre
                  </button>
                  {joinRow}
                </form>
              </>
            ) : checkingZemew ? (
              <p className="muted login-sub">Checking your Zemew account…</p>
            ) : (
              <div className="login-actions">
                <p className="muted login-sub">
                  {zemewLoggedIn
                    ? 'You are already signed in on Zemew. Enter the theater, join a private house, or start your own.'
                    : 'Sign in with your Zemew account to sit down. Your login name is your seat name.'}
                </p>
                {error && <div className="error-banner">{error}</div>}
                {!ssoReady && (
                  <div className="error-banner">Zemew sign-in is not configured on this theater yet.</div>
                )}
                {zemewLoggedIn ? (
                  <a
                    className="btn btn-primary login-enter-btn"
                    href={ssoHref}
                    onClick={() => persistJoinCode(partyCode)}
                  >
                    Enter the theater
                  </a>
                ) : (
                  <a
                    className="btn btn-primary login-enter-btn"
                    href={publicSsoHref}
                    onClick={() => persistJoinCode('')}
                  >
                    Sign in with Zemew
                  </a>
                )}
                <button
                  className="btn btn-gold login-enter-btn"
                  type="button"
                  disabled={busy || !ssoReady}
                  onClick={() => startPrivateHouse()}
                >
                  Create private theatre
                </button>
                {joinRow}
                {onWatchInstead && (
                  <button
                    type="button"
                    className="login-host-link"
                    onClick={() => {
                      persistJoinCode(partyCode);
                      onWatchInstead();
                    }}
                  >
                    Just watch
                  </button>
                )}
              </div>
            )}
            <button type="button" className="login-host-link" onClick={openHostLogin}>
              Host login
            </button>
          </>
        ) : (
          <>
            <p className="muted login-sub">Sign in to host movie night.</p>
            <form onSubmit={handleHostLogin} className="login-form">
              <label>
                <span>Username</span>
                <input
                  className="field"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </label>
              <label>
                <span>Password</span>
                <input
                  className="field"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <button
                className="btn btn-gold login-enter-btn"
                type="button"
                disabled={busy}
                onClick={() => queuePrivateHouse()}
              >
                Create private theatre
              </button>
              {joinRow}
              {error && <div className="error-banner">{error}</div>}
              <button className="btn btn-primary login-enter-btn" disabled={busy} type="submit">
                {busy ? 'Signing in…' : 'Host sign in'}
              </button>
            </form>
            <button
              type="button"
              className="login-host-link"
              onClick={() => {
                persistJoinCode(partyCode);
                setMode('join');
                setError(null);
                setPassword('');
                const url = new URL(window.location.href);
                url.searchParams.delete('host');
                window.history.replaceState({}, '', url.pathname + url.search);
              }}
            >
              Back to join
            </button>
          </>
        )}
      </div>
    </div>
  );
}
